import isNull from "lodash/isNull";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { bugNotif } from "../../api/bugsnag";
import { getErrorMsg } from "../../../Common/utils/common";
import { useWebUserStore } from "../user/users";
import { shallow } from "zustand/shallow";
import useWLConfigStore from "../wlConfig/store";
import useFailedPaymentOfferStore from "./failedPaymentOfferStore";
import { PromoTypeConfig } from "../../../Common/models/promos";
import Stripe from "stripe";
import useUserSubscriptionsStore from "../subscriptions/store";
import { debounce, isEmpty } from "lodash";
import {
  getPriceWithStripeCreditsValue,
  getWLPromoEntries,
  isPromoValid,
} from "../../services/promo";
import useWLPromoStore from "../../components/WLPromoField/store";
import { trackUserOfferedFixBilling } from "../../api/user";
import { resolveOfferViewedAtFromSearchParams } from "../../utils/resolveOfferViewedAtFromSearchParams";

/**
 * Admin-generated fix-billing links may force a Billing Issue promo via `?promo=`.
 * Override is only active when `offerViewedAt=admin` (see resolveOfferViewedAtFromSearchParams).
 */
const getAdminOverridePromoCode = (): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  if (resolveOfferViewedAtFromSearchParams(searchParams) !== "admin") {
    return null;
  }
  const rawPromo = searchParams.get("promo");
  if (isNull(rawPromo) || isEmpty(rawPromo.trim())) {
    return null;
  }
  return rawPromo.toUpperCase().trim();
};

export const useFailedPaymentShowBannerProvider = (): void => {
  const [wlConfig] = useWLConfigStore((state) => [state.wlConfig], shallow);

  const [
    user,
    stripeActiveSubscription,
    latestFailedStripePaymentAttempt,
    hasBillingIssue,
    setHasBillingIssue,
  ] = useWebUserStore(
    (state) => [
      state.user,
      state.stripeActiveSubscription,
      state.latestFailedStripePaymentAttempt,
      state.hasBillingIssue,
      state.setHasBillingIssue,
    ],
    shallow
  );
  const [subscriptionList] = useUserSubscriptionsStore(
    (state) => [state.subscriptionList],
    shallow
  );

  const [
    promoCode,
    failedPaymentSubscription,
    setIsLastFailedPaymentOffer,
    setPromoCode,
    setFailedPaymentSubscription,
    setPercentOffer,
    setBonusEntries,
    setFailedPaymentOfferModal,
    resetFailedPaymentOfferStore,
  ] = useFailedPaymentOfferStore(
    (state) => [
      state.promoCode,
      state.failedPaymentSubscription,
      state.setIsLastFailedPaymentOffer,
      state.setPromoCode,
      state.setFailedPaymentSubscription,
      state.setPercentOffer,
      state.setBonusEntries,
      state.setFailedPaymentOfferModal,
      state.resetStore,
    ],
    shallow
  );

  const timerShowOfferRef = useRef<null | NodeJS.Timeout>(null);

  const [validWLPromo, setValidWLPromo, setValidPromoCode] = useWLPromoStore(
    (store) => [
      store.validWLPromo,
      store.setValidWLPromo,
      store.setValidPromoCode,
    ],
    shallow
  );

  const validatePromo = useCallback(
    debounce(async (code: string) => {
      if (isEmpty(code) || !failedPaymentSubscription) {
        setValidWLPromo(null);
        return true;
      }
      setValidWLPromo(null);

      const isValidWLPromo = await validateWLPromoCode(code, {
        forBillingIssue: true,
        subscriptionId: failedPaymentSubscription.docId,
      });
      return isValidWLPromo || "Invalid promo code";
    }, 400),
    [failedPaymentSubscription]
  );

  const validateWLPromoCode = useCallback(
    async (code: string, promoConfig: PromoTypeConfig) => {
      try {
        const result = await isPromoValid({
          code,
          ...promoConfig,
        });
        if (isNull(result)) {
          throw new Error("Invalid promo");
        }

        const testedPromoCode = result.code;
        setValidWLPromo(result);
        setValidPromoCode(result.code);

        return !isEmpty(testedPromoCode);
      } catch (error) {
        setValidWLPromo(null);
        bugNotif(
          "validateWLPromoCode - failed payment",
          getErrorMsg(error).rawErrorMsg
        );
        return false;
      }
    },
    []
  );

  useEffect(() => {
    if (
      !!stripeActiveSubscription &&
      !!subscriptionList &&
      !!subscriptionList.length
    ) {
      setFailedPaymentSubscription(
        subscriptionList.find(
          (_subscription) =>
            _subscription.docId === stripeActiveSubscription.plan?.product
        )
      );
    } else {
      setFailedPaymentSubscription(undefined);
    }
  }, [stripeActiveSubscription, subscriptionList]);

  const trackUserOfferedPromo = debounce(
    async (userId: string, promoCode: string, revenue: number) => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const offerViewedAt =
          resolveOfferViewedAtFromSearchParams(searchParams);

        await trackUserOfferedFixBilling({
          userId,
          promoCode,
          revenue,
          offerViewedAt,
        });
      } catch (error) {
        bugNotif(
          "web - trackUserOfferedPromo - failed payments",
          getErrorMsg(error).rawErrorMsg
        );
      }
    },
    500
  );

  useEffect(() => {
    if (validWLPromo && failedPaymentSubscription) {
      const { stripeCreditDiscount, isStripeCreditPercentOff } = validWLPromo;
      const bonusEntries = getWLPromoEntries(
        validWLPromo,
        failedPaymentSubscription
      );

      if (!!isStripeCreditPercentOff && !isEmpty(user) && !isEmpty(promoCode)) {
        // both percent and bonus entries for this offer
        setPercentOffer(stripeCreditDiscount || 0);
      }
      if (bonusEntries) {
        setBonusEntries(bonusEntries);
      }

      try {
        const user = useWebUserStore.getState().user;
        const latestFailedStripePaymentAttempt =
          useWebUserStore.getState().latestFailedStripePaymentAttempt;
        if (
          user &&
          validWLPromo &&
          validWLPromo.billingIssue &&
          validWLPromo.code &&
          latestFailedStripePaymentAttempt
        ) {
          const priceBasis = failedPaymentSubscription.price;
          const revenue = getPriceWithStripeCreditsValue(
            priceBasis,
            validWLPromo.stripeCreditDiscount || 0,
            !!validWLPromo.isStripeCreditPercentOff
          );
          // Track matrix and Admin URL-override offers (both are Billing Issue promos).
          trackUserOfferedPromo(user.docId, validWLPromo.code, revenue);
        }
      } catch (error) {
        bugNotif(
          "web - sending tracker for fix payment",
          getErrorMsg(error).rawErrorMsg
        );
      }
    }
  }, [validWLPromo, failedPaymentSubscription]);

  const isPromoForUserUpdated = useMemo(() => {
    if (
      !hasBillingIssue ||
      !latestFailedStripePaymentAttempt ||
      !wlConfig ||
      !failedPaymentSubscription
    ) {
      return false;
    }

    const overridePromoCode = getAdminOverridePromoCode();
    if (overridePromoCode) {
      // Admin URL override is authoritative — sync until store matches (valid or not).
      return promoCode !== overridePromoCode;
    }

    const { attempt_count } = latestFailedStripePaymentAttempt?.data
      .object as Stripe.Invoice;
    const currentAttemptPromos =
      wlConfig.billingIssuePromoConfig[attempt_count];
    const configPromoForSubscription =
      currentAttemptPromos?.[failedPaymentSubscription.docId];

    const trimmedConfigPromo = configPromoForSubscription?.trim();
    if (trimmedConfigPromo) {
      return promoCode !== trimmedConfigPromo;
    }
    return !isEmpty(promoCode);
  }, [
    validWLPromo,
    promoCode,
    hasBillingIssue,
    latestFailedStripePaymentAttempt,
    wlConfig,
    failedPaymentSubscription,
  ]);

  useEffect(() => {
    if (
      hasBillingIssue &&
      latestFailedStripePaymentAttempt &&
      wlConfig &&
      failedPaymentSubscription &&
      isPromoForUserUpdated
    ) {
      const { attempt_count } = latestFailedStripePaymentAttempt?.data
        .object as Stripe.Invoice;

      const overridePromoCode = getAdminOverridePromoCode();
      let offerPromoCode: string | null = null;

      if (overridePromoCode) {
        // Admin override (offerViewedAt=admin + promo) ignores attempt × tier matrix.
        offerPromoCode = overridePromoCode;
      } else if (
        failedPaymentSubscription.docId &&
        wlConfig.billingIssuePromoConfig
      ) {
        const attemptPromos = wlConfig.billingIssuePromoConfig[attempt_count];
        const configPromo =
          attemptPromos?.[failedPaymentSubscription.docId];
        if (configPromo && configPromo.trim()) {
          offerPromoCode = configPromo.trim();
        }
      }

      if (offerPromoCode) {
        if (!isNull(timerShowOfferRef.current)) {
          clearTimeout(timerShowOfferRef.current);
        }

        setPromoCode(offerPromoCode);
        validatePromo(offerPromoCode);
        setIsLastFailedPaymentOffer(attempt_count >= 7);
      } else {
        setPromoCode("");
        setValidWLPromo(null);
        console.log(
          `No promo found in BillingIssuePromoConfig for attempt ${attempt_count} and subscription ${failedPaymentSubscription?.name}`
        );
      }
    }
  }, [
    latestFailedStripePaymentAttempt,
    hasBillingIssue,
    wlConfig,
    failedPaymentSubscription,
    isPromoForUserUpdated,
  ]);

  useEffect(() => {
    if (!hasBillingIssue && promoCode) {
      resetFailedPaymentOfferStore();
    }
  }, [hasBillingIssue, promoCode]);
};
